import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { currentLanguage, isLanguage, type Language, setLanguage } from '@/i18n';
import { ApiRequestError, setUnauthorizedHandler } from '@/lib/api-client';
import { logger } from '@/lib/logger';
import { outbox } from '@/lib/outbox';
import { session } from '@/lib/session';
import type { Role, User } from '@/types/api';
import { authApi } from './api';

type AuthState = {
  user: User | null;
  /** The last session ended because the token expired (not a logout). */
  expired: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  /** Switch the UI language; when signed in, also save it to the account. */
  changeLanguage: (lang: Language) => Promise<void>;
  /** Platform administrator: work in this clinic's settings and users (support mode). */
  enterClinic: (clinicId: string) => Promise<void>;
  /** Leave support mode for the platform console. */
  exitClinic: () => void;
  /** Save the profile (name, password) online; throws when the server refuses. */
  updateProfile: (patch: {
    name?: string;
    password?: string;
    currentPassword?: string;
  }) => Promise<void>;
};

/** A language switch that has not reached the account yet (made offline). */
const UNSAVED_KEY = 'dentlog.lang.unsaved';
const unsavedLanguage = {
  get(): Language | null {
    try {
      const v = localStorage.getItem(UNSAVED_KEY);
      return isLanguage(v) ? v : null;
    } catch {
      return null;
    }
  },
  set(lang: Language) {
    try {
      localStorage.setItem(UNSAVED_KEY, lang);
    } catch {
      // storage unavailable: the switch then lasts until the next refresh
    }
  },
  clear() {
    try {
      localStorage.removeItem(UNSAVED_KEY);
    } catch {
      // ignore
    }
  },
};

const AuthContext = createContext<AuthState | null>(null);

/** The account's language wins over the device's. */
function applyAccountLanguage(user: User) {
  if (isLanguage(user.locale) && user.locale !== currentLanguage()) void setLanguage(user.locale);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Start from the cached user so the app opens offline.
  const [user, setUser] = useState<User | null>(() => (session.token() ? session.user() : null));

  const [expired, setExpired] = useState(false);

  /** Explicit logout: health data must not outlive the session on a shared device. */
  const logout = useCallback(() => {
    const id = session.user()?.id;
    session.clear();
    if (id) void outbox.removeByAuthor(id).catch((err) => logger.warn('outbox clear failed', err));
    setExpired(false);
    setUser(null);
  }, []);

  /** Token rejected (expired): sign in again, but keep unsynced entries queued. */
  const expire = useCallback(() => {
    session.clear();
    setExpired(true);
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(expire);
  }, [expire]);

  // Refresh the profile (role or clinic may have changed).
  useEffect(() => {
    if (!session.token()) return;
    authApi
      .me()
      .then(async (fresh) => {
        const pending = unsavedLanguage.get();
        if (pending && pending !== fresh.locale) {
          // A switch made offline: save it now rather than reverting it.
          const saved = await authApi.updateMe({ locale: pending }).catch(() => null);
          if (saved) unsavedLanguage.clear();
          session.saveUser(saved ?? { ...fresh, locale: pending });
          setUser(saved ?? { ...fresh, locale: pending });
          return;
        }
        unsavedLanguage.clear();
        session.saveUser(fresh);
        setUser(fresh);
        applyAccountLanguage(fresh);
      })
      .catch((err) => {
        if (!(err instanceof ApiRequestError && err.isNetwork)) logger.warn('me failed', err);
      });
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const { token, user: u } = await authApi.login(email, password);
    session.save(token, u);
    setExpired(false);
    setUser(u);
    applyAccountLanguage(u);
  }, []);

  const enterClinic = useCallback(async (clinicId: string) => {
    const { token, user: u } = await authApi.enterClinic(clinicId);
    session.enterSupport(token, u);
    setUser(u);
  }, []);

  const exitClinic = useCallback(() => {
    const platform = session.exitSupport();
    setExpired(false);
    setUser(platform);
  }, []);

  const changeLanguage = useCallback(async (lang: Language) => {
    await setLanguage(lang);
    const cached = session.user();
    if (!(session.token() && cached)) return;
    // Keep the cached profile in step with the device.
    session.saveUser({ ...cached, locale: lang });
    try {
      const fresh = await authApi.updateMe({ locale: lang });
      session.saveUser(fresh);
      setUser(fresh);
      unsavedLanguage.clear();
    } catch (err) {
      // Offline or refused: not an error for the user. Remember it, so the next
      // profile refresh saves it instead of switching back to the account's language.
      unsavedLanguage.set(lang);
      logger.warn('saving the language to the account failed', err);
    }
  }, []);

  const updateProfile = useCallback(
    async (patch: { name?: string; password?: string; currentPassword?: string }) => {
      const fresh = await authApi.updateMe(patch);
      session.saveUser(fresh);
      setUser(fresh);
    },
    []
  );

  const value = useMemo(
    () => ({
      user,
      expired,
      login,
      logout,
      enterClinic,
      exitClinic,
      changeLanguage,
      updateProfile,
    }),
    [user, expired, login, logout, enterClinic, exitClinic, changeLanguage, updateProfile]
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

export const ROLES: readonly Role[] = ['ADMIN', 'DENTIST', 'ASSISTANT'];
